import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, Popover, RadioButtonsGroup } from 'sonata-react-components';
import { useTheme } from 'styled-components';

import './inventoryListSort.scss';
import '@/components/common/ContextualPanel/contextualPanel.scss';

import type { InventoryOperation, SortDirection } from '../../AppAndSiteMarketplace.types';

type Props = {
  disabled: boolean;
  id: string;
  onChange: (direction: SortDirection) => void;
  operation: InventoryOperation;
  value?: SortDirection;
};

/** Sort button that opens a popover to pick the sort direction of a column. */
export function InventoryListSort({ disabled, id, onChange, value, operation }: Props) {
  const { formatMessage } = useIntl();
  const { themeContrast } = useTheme() as {
    themeContrast: 'light' | 'dark' | 'darkAI';
  };
  const [isOpen, setIsOpen] = useState(false);

  const title = formatMessage({
    defaultMessage: 'Sort',
    id: 'appAndSiteMarketplace.inventory.list.sort.button'
  });

  const handleChange = (direction: string) => {
    onChange(direction as SortDirection);
    setIsOpen(false);
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen} placement="top-end">
      <Popover.Trigger asChild>
        <ButtonUI
          disabled={disabled}
          icon="fa-solid fa-sort"
          iconClassName={`inventoryListSort__ButtonIcon--${operation}`}
          id={`${id}_sortButton`}
          onClick={() => setIsOpen(!isOpen)}
          shape="link"
          size="ultraSmall"
          title={title}
          type="secondary"
        />
      </Popover.Trigger>
      <Popover.Content id={`${id}_sortMenu`} style={{ zIndex: 1000 }}>
        <CustomThemeProvider themeContrast={themeContrast}>
          <div className="contextualPanel inventorySortMenu">
            <RadioButtonsGroup
              id={`${id}_sort`}
              inColumn
              items={[
                {
                  id: 'asc',
                  itemClassName: value === 'asc' ? 'inventorySortMenu__item--selected' : undefined,
                  text: formatMessage({
                    defaultMessage: 'Sort A to Z',
                    id: 'appAndSiteMarketplace.inventory.list.sort.asc'
                  })
                },
                {
                  id: 'desc',
                  itemClassName: value === 'desc' ? 'inventorySortMenu__item--selected' : undefined,
                  text: formatMessage({
                    defaultMessage: 'Sort Z to A',
                    id: 'appAndSiteMarketplace.inventory.list.sort.desc'
                  })
                }
              ]}
              onChange={handleChange}
              selectedItem={value}
              value={value}
              withContainer={false}
            />
          </div>
        </CustomThemeProvider>
      </Popover.Content>
    </Popover>
  );
}
